/**
 * Export Utilities
 *
 * Functions for exporting analytics data to PDF and Excel formats.
 */

import { formatAmount, latnLocale } from './numberFormat';

export interface ExportData {
  title: string;
  dateRange: {
    start: Date;
    end: Date;
  };
  overview: {
    totalViews: number;
    totalClicks: number;
    totalContacts: number;
    totalSaves: number;
    conversionRate: number;
  };
  timeSeries: Array<{
    date: string;
    views: number;
    clicks: number;
    contacts: number;
    saves: number;
  }>;
  propertyAnalytics: Array<{
    propertyId: string;
    propertyTitle: string;
    views: number;
    clicks: number;
    contacts: number;
    saves: number;
  }>;
}

export type ExportFormat = 'pdf' | 'excel' | 'csv';

/**
 * Format date for display
 */
function formatDate(date: Date, locale: string = 'en-US'): string {
  return date.toLocaleDateString(latnLocale(locale), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Generate CSV content from data
 */
function generateCSV(data: ExportData, locale: string): string {
  const lines: string[] = [];
  const isArabic = locale === 'ar-SA';

  // Header
  lines.push(data.title);
  lines.push(`${isArabic ? 'الفترة' : 'Period'}: ${formatDate(data.dateRange.start, locale)} - ${formatDate(data.dateRange.end, locale)}`);
  lines.push('');

  // Overview section
  lines.push(isArabic ? '=== ملخص الأداء ===' : '=== Performance Summary ===');
  lines.push(`${isArabic ? 'إجمالي المشاهدات' : 'Total Views'},${data.overview.totalViews}`);
  lines.push(`${isArabic ? 'إجمالي النقرات' : 'Total Clicks'},${data.overview.totalClicks}`);
  lines.push(`${isArabic ? 'إجمالي التواصل' : 'Total Contacts'},${data.overview.totalContacts}`);
  lines.push(`${isArabic ? 'إجمالي الحفظ' : 'Total Saves'},${data.overview.totalSaves}`);
  lines.push(`${isArabic ? 'معدل التحويل' : 'Conversion Rate'},${data.overview.conversionRate.toFixed(2)}%`);
  lines.push('');

  // Time series section
  lines.push(isArabic ? '=== البيانات اليومية ===' : '=== Daily Data ===');
  lines.push(
    isArabic
      ? 'التاريخ,المشاهدات,النقرات,التواصل,الحفظ'
      : 'Date,Views,Clicks,Contacts,Saves'
  );
  data.timeSeries.forEach((row) => {
    lines.push(`${row.date},${row.views},${row.clicks},${row.contacts},${row.saves}`);
  });
  lines.push('');

  // Property analytics section
  lines.push(isArabic ? '=== أداء العقارات ===' : '=== Property Performance ===');
  lines.push(
    isArabic
      ? 'العقار,المشاهدات,النقرات,التواصل,الحفظ'
      : 'Property,Views,Clicks,Contacts,Saves'
  );
  data.propertyAnalytics.forEach((prop) => {
    lines.push(`"${prop.propertyTitle.replace(/"/g, '""')}",${prop.views},${prop.clicks},${prop.contacts},${prop.saves}`);
  });

  return lines.join('\n');
}

/**
 * Generate HTML content for PDF export
 */
function generatePDFHTML(data: ExportData, locale: string): string {
  const isArabic = locale === 'ar-SA';
  const dir = isArabic ? 'rtl' : 'ltr';

  return `
    <!DOCTYPE html>
    <html dir="${dir}" lang="${isArabic ? 'ar' : 'en'}">
    <head>
      <meta charset="UTF-8">
      <title>${escapeHtml(data.title)}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          padding: 40px;
          color: #0D201C;
          direction: ${dir};
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #004D40;
        }
        .header h1 {
          color: #004D40;
          font-size: 24px;
          margin-bottom: 10px;
        }
        .header .date-range {
          color: #455855;
          font-size: 14px;
        }
        .section {
          margin-bottom: 30px;
        }
        .section-title {
          font-size: 18px;
          color: #0D201C;
          margin-bottom: 15px;
          padding-bottom: 8px;
          border-bottom: 1px solid #E0E9E7;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
          margin-bottom: 20px;
        }
        .stat-card {
          background: #F2F8F7;
          padding: 15px;
          border-radius: 8px;
          text-align: center;
        }
        .stat-value {
          font-size: 24px;
          font-weight: bold;
          color: #004D40;
        }
        .stat-label {
          font-size: 12px;
          color: #455855;
          margin-top: 5px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        th, td {
          padding: 10px;
          text-align: ${isArabic ? 'right' : 'left'};
          border-bottom: 1px solid #E0E9E7;
        }
        th {
          background: #F2F8F7;
          font-weight: 600;
          color: #0D201C;
        }
        tr:hover {
          background: #F8FAFA;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
          color: #617672;
          font-size: 11px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${escapeHtml(data.title)}</h1>
        <div class="date-range">
          ${formatDate(data.dateRange.start, locale)} - ${formatDate(data.dateRange.end, locale)}
        </div>
      </div>

      <div class="section">
        <h2 class="section-title">${isArabic ? 'ملخص الأداء' : 'Performance Summary'}</h2>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">${formatAmount(data.overview.totalViews)}</div>
            <div class="stat-label">${isArabic ? 'المشاهدات' : 'Views'}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${formatAmount(data.overview.totalClicks)}</div>
            <div class="stat-label">${isArabic ? 'النقرات' : 'Clicks'}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${formatAmount(data.overview.totalContacts)}</div>
            <div class="stat-label">${isArabic ? 'التواصل' : 'Contacts'}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${formatAmount(data.overview.totalSaves)}</div>
            <div class="stat-label">${isArabic ? 'الحفظ' : 'Saves'}</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${data.overview.conversionRate.toFixed(1)}%</div>
            <div class="stat-label">${isArabic ? 'معدل التحويل' : 'Conversion Rate'}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <h2 class="section-title">${isArabic ? 'أداء العقارات' : 'Property Performance'}</h2>
        <table>
          <thead>
            <tr>
              <th>${isArabic ? 'العقار' : 'Property'}</th>
              <th>${isArabic ? 'المشاهدات' : 'Views'}</th>
              <th>${isArabic ? 'النقرات' : 'Clicks'}</th>
              <th>${isArabic ? 'التواصل' : 'Contacts'}</th>
              <th>${isArabic ? 'الحفظ' : 'Saves'}</th>
            </tr>
          </thead>
          <tbody>
            ${data.propertyAnalytics
              .map(
                (prop) => `
              <tr>
                <td>${escapeHtml(prop.propertyTitle)}</td>
                <td>${formatAmount(prop.views)}</td>
                <td>${formatAmount(prop.clicks)}</td>
                <td>${formatAmount(prop.contacts)}</td>
                <td>${formatAmount(prop.saves)}</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      </div>

      <div class="footer">
        ${isArabic ? 'تم إنشاء هذا التقرير بواسطة صفقات 24' : 'Report generated by Safaqat 24'}
        <br>
        ${new Date().toLocaleDateString(latnLocale(locale))}
      </div>
    </body>
    </html>
  `;
}


/**
 * SECURITY (security-guidance): everything interpolated into the print-window
 * HTML below must be escaped — property titles are advertiser-supplied text,
 * and document.write of unescaped input is an XSS sink in a same-origin window.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Download a file
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export data to CSV format
 */
export function exportToCSV(data: ExportData, locale: string = 'en-US'): void {
  const csv = generateCSV(data, locale);
  const filename = `safaqat24-analytics-${new Date().toISOString().split('T')[0]}.csv`;
  downloadFile('\ufeff' + csv, filename, 'text/csv;charset=utf-8'); // BOM for Excel UTF-8
}

/**
 * Export data to PDF format (opens print dialog)
 */
export function exportToPDF(data: ExportData, locale: string = 'en-US'): void {
  const html = generatePDFHTML(data, locale);
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
}

/**
 * Export data to Excel format (XLSX-like CSV with proper encoding)
 */
export function exportToExcel(data: ExportData, locale: string = 'en-US'): void {
  const csv = generateCSV(data, locale);
  const filename = `safaqat24-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
  // Using CSV with .xlsx extension - for true XLSX, would need xlsx library
  downloadFile('\ufeff' + csv, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

/**
 * Main export function
 */
export async function exportAnalytics(
  data: ExportData,
  format: ExportFormat,
  locale: string = 'en-US'
): Promise<void> {
  switch (format) {
    case 'pdf':
      exportToPDF(data, locale);
      break;
    case 'excel':
      exportToExcel(data, locale);
      break;
    case 'csv':
      exportToCSV(data, locale);
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}
